import React, { useState, useEffect } from "react";
import { ComboBox } from "@progress/kendo-react-dropdowns";
import axios from "axios";
import { BASE_URL } from "../../../constants";
import { toast } from "react-toastify";

const CurrencySelectionPopup = ({ isOpen, onClose, onCurrencySelect, currentCurrency }) => {
  const [baseCurrencies, setBaseCurrencies] = useState([]);
  const [filteredBaseCurrencies, setFilteredBaseCurrencies] = useState([]);
  const [selectedCurrency, setSelectedCurrency] = useState(currentCurrency || null);
  const [loading, setLoading] = useState(false);

  // Load base currencies from API
  const loadBaseCurrencies = async (searchText = "~ALL") => {
    try {
      const response = await axios.get(
        `${BASE_URL}/Currency/getallcurrency?searchText=${searchText}&pageNo=0&pageSize=1000`
      );
      
      const currencies = response.data.data
        .filter((currency) => currency.baseCurrency && currency.isActive)
        .map((currency) => ({
          code: currency.code,
          name: currency.name,
        }));
      
      setBaseCurrencies(currencies);
      setFilteredBaseCurrencies(currencies);
      
      // Set default selection if none exists
      if (!selectedCurrency && currencies.length > 0) {
        setSelectedCurrency(currencies[0]);
      }
    } catch (error) {
      console.error("Error loading base currencies:", error);
      toast.error("Failed to load currencies");
    }
  };

  // Filter currencies based on search
  const filterCurrencies = (e) => {
    const searchValue = e.filter?.value || "";
    if (searchValue === "") {
      setFilteredBaseCurrencies(baseCurrencies);
    } else {
      const filtered = baseCurrencies.filter(
        (currency) =>
          currency.name.toLowerCase().includes(searchValue.toLowerCase()) ||
          currency.code.toLowerCase().includes(searchValue.toLowerCase())
      );
      setFilteredBaseCurrencies(filtered);
    }
  };

  // Handle currency selection
  const handleCurrencyChange = (e) => {
    setSelectedCurrency(e.value);
  };

  // Handle OK button click
  const handleOkClick = async () => {
    if (!selectedCurrency) {
      toast.error("Please select a currency");
      return;
    }

    setLoading(true);
    try {
      // Get current date - 1 for exchange rate (as requested)
      const currentDate = new Date();
      const previousDay = new Date(currentDate);
      previousDay.setDate(currentDate.getDate() - 1);
      const dateParam = previousDay.toISOString().split('T')[0]; // YYYY-MM-DD format
      
      // Fetch exchange rate for selected currency
      const exchangeResponse = await axios.get(
        `${BASE_URL}/api/ExchangeRate/getall?baseCurrencyCode=${selectedCurrency.code}&searchText=~ALL&date=${dateParam}&pageSize=1000`
      );

      const exchangeRates = exchangeResponse.data.data || [];
      
      if (exchangeRates.length === 0) {
        toast.warning(`No exchange rates found for ${selectedCurrency.name}. Using original currency values.`);
      }
      
      // Pass selected currency and exchange rates to parent
      onCurrencySelect(selectedCurrency, exchangeRates);
      onClose();
    } catch (error) {
      console.error("Error fetching exchange rates:", error);
      if (error.response?.status === 404) {
        toast.warning(`No exchange rates found for ${selectedCurrency.name}. Using original currency values.`);
        onCurrencySelect(selectedCurrency, []);
        onClose();
      } else {
        toast.error("Failed to fetch exchange rates for selected currency");
      }
    } finally {
      setLoading(false);
    }
  };

  // Initialize data when popup opens
  useEffect(() => {
    if (isOpen) {
      loadBaseCurrencies();
      setSelectedCurrency(currentCurrency);
    }
  }, [isOpen, currentCurrency]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-lg w-96 max-w-md mx-4">
        <h2 className="text-lg font-semibold mb-2 text-gray-800">Select Currency for Performa Invoice</h2>
        {/* <p className="text-sm text-gray-600 mb-4">
          Choose a base currency to display amounts with current exchange rates, or skip to use original currency.
        </p> */}
        
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Base Currency
          </label>
          <div className="relative">
            <ComboBox
              data={filteredBaseCurrencies}
              textField="name"
              dataItemKey="code"
              value={selectedCurrency}
              onChange={handleCurrencyChange}
              onFilterChange={filterCurrencies}
              className="w-full border border-gray-300 rounded-md"
              placeholder="Select a currency"
              suggest={true}
              clearButton={true}
              filterable={true}
            />
          </div>
          {selectedCurrency && (
            <div className="text-sm text-gray-600 mt-2">
              <p>Exchange rates will be fetched for {selectedCurrency.name} ({selectedCurrency.code})</p>
              <p className="text-xs text-gray-500">
                Using exchange rates from: {new Date(new Date().getTime() - 24 * 60 * 60 * 1000).toLocaleDateString()}
              </p>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button
            type="button"
            onClick={() => {
              // If no currency is selected, close without selection
              onClose();
            }}
            className="px-4 py-2 text-gray-600 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 transition-colors duration-200"
            disabled={loading}
          >
            Skip
          </button>
          <button
            type="button"
            onClick={handleOkClick}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            disabled={loading || !selectedCurrency}
          >
            {loading ? "Loading..." : "Apply Currency"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CurrencySelectionPopup;
